import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';

type ResponseJson = Record<string, unknown>;
type SentryTagValue = string | number | boolean | null | undefined;
type FingerprintValue = string | number | boolean | null | undefined;

interface FunctionInvokeSentryContextOptions {
  tags?: Record<string, SentryTagValue>;
  extra?: Record<string, unknown>;
}

export interface FunctionInvokeErrorDetails {
  functionName?: string;
  status?: number;
  responseBody?: string;
  responseJson?: ResponseJson;
  errorCode?: string;
  errorClass?: string;
  rpcName?: string;
  requestId?: string;
  attemptCount?: number;
  isAbort?: boolean;
  isFetchError?: boolean;
}

function asTrimmedString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

export function getFunctionInvokeErrorFingerprint(details?: FunctionInvokeErrorDetails): string[] | undefined {
  const functionName = asTrimmedString(details?.functionName);
  if (!functionName) {
    return undefined;
  }

  const fingerprint = ['edge-function-root-cause', functionName];
  const rpcName = asTrimmedString(details?.rpcName);
  if (rpcName) {
    fingerprint.push(rpcName);
  }

  const errorCode = asTrimmedString(details?.errorCode);
  if (errorCode) {
    fingerprint.push(errorCode);
  } else if (typeof details?.status === 'number') {
    if (details.status === 401 || details.status === 403) {
      fingerprint.push('auth');
    } else if (details.status >= 500) {
      fingerprint.push('unexpected');
    } else {
      fingerprint.push(String(details.status));
    }
  }

  return fingerprint;
}

export class FunctionInvokeError extends Error {
  readonly functionName?: string;
  readonly status?: number;
  readonly responseBody?: string;
  readonly responseJson?: ResponseJson;
  readonly errorCode?: string;
  readonly errorClass?: string;
  readonly rpcName?: string;
  readonly requestId?: string;
  readonly attemptCount?: number;
  readonly isAbort?: boolean;
  readonly isFetchError?: boolean;

  constructor(message: string, details: FunctionInvokeErrorDetails = {}) {
    super(message);
    this.name = 'FunctionInvokeError';
    this.functionName = details.functionName;
    this.status = details.status;
    this.responseBody = details.responseBody;
    this.responseJson = details.responseJson;
    this.errorCode = details.errorCode;
    this.errorClass = details.errorClass;
    this.rpcName = details.rpcName;
    this.requestId = details.requestId;
    this.attemptCount = details.attemptCount;
    this.isAbort = details.isAbort;
    this.isFetchError = details.isFetchError;
  }
}

function isAbortErrorMessage(message: string | undefined): boolean {
  if (!message) {
    return false;
  }

  return /\babort(error|ed)?\b/i.test(message);
}

function isAbortErrorLike(
  error: unknown,
  seen: WeakSet<object> = new WeakSet(),
): boolean {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return typeof error === 'string' && isAbortErrorMessage(error);
  }

  if (seen.has(error as object)) {
    return false;
  }
  seen.add(error as object);

  if (
    'name' in error &&
    typeof error.name === 'string' &&
    error.name === 'AbortError'
  ) {
    return true;
  }

  if (
    'isAbort' in error &&
    typeof error.isAbort === 'boolean' &&
    error.isAbort
  ) {
    return true;
  }

  if (
    'message' in error &&
    typeof error.message === 'string' &&
    isAbortErrorMessage(error.message)
  ) {
    return true;
  }

  if ('cause' in error && isAbortErrorLike(error.cause, seen)) {
    return true;
  }

  if ('context' in error && isAbortErrorLike(error.context, seen)) {
    return true;
  }

  return false;
}

export function isFunctionInvokeAbortError(error: unknown): boolean {
  return isAbortErrorLike(error);
}

/**
 * True for a transport-level Supabase Edge Function fetch failure — i.e. the
 * browser never received a valid HTTP response. This is `FunctionsFetchError`
 * ("Failed to send a request to the Edge Function"), distinct from
 * `FunctionsHttpError` (the function ran and returned a non-2xx).
 */
export function isFunctionFetchError(error: unknown): boolean {
  if (error instanceof FunctionInvokeError && error.isFetchError) {
    return true;
  }

  if (error instanceof FunctionsFetchError) {
    return true;
  }

  return Boolean(
    error &&
      typeof error === 'object' &&
      'name' in error &&
      (error as { name?: unknown }).name === 'FunctionsFetchError',
  );
}

/**
 * True only when a transport-level fetch failure happens while the browser
 * reports itself offline. `navigator.onLine === false` is a definitive "this
 * device has no network" signal, so the request was never going to reach the
 * function — capturing it is noise, not an actionable defect. Online fetch
 * failures (CORS, DNS, edge outage) are deliberately NOT suppressed.
 */
export function isOfflineFunctionFetchError(error: unknown): boolean {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) {
    return false;
  }

  return isFunctionFetchError(error);
}

export const EXPECTED_RECOVERY_PATH_DENIAL_CODES: ReadonlySet<string> = new Set([
  'subscription_inactive',
  'org_access_denied',
  'organization_access_denied',
  // ENT-5616: a customer_service member refused the MSP's own org data.
  'customer_service_own_org_forbidden',
]);

/**
 * Classifies 403s that the backend has explicitly tagged as an expected
 * recovery-path denial via a canonical error code. See ENT-901.
 *
 * Deliberately does NOT match on the bare "You do not have access to this
 * organization" message: that text is also returned for genuine foreign-org
 * 403s, so suppressing it would hide real unauthorized access events.
 */
export function isExpectedRecoveryPathDenial(error: unknown): boolean {
  if (!(error instanceof FunctionInvokeError)) {
    return false;
  }

  if (error.status !== 403) {
    return false;
  }

  if (error.errorCode && EXPECTED_RECOVERY_PATH_DENIAL_CODES.has(error.errorCode)) {
    return true;
  }

  const responseJsonCode = error.responseJson && typeof error.responseJson.code === 'string'
    ? error.responseJson.code
    : undefined;
  return Boolean(
    responseJsonCode && EXPECTED_RECOVERY_PATH_DENIAL_CODES.has(responseJsonCode),
  );
}

export function isSubscriptionInactiveError(error: unknown): boolean {
  if (!(error instanceof FunctionInvokeError)) {
    return false;
  }

  if (error.errorCode === 'subscription_inactive') {
    return true;
  }

  const responseJsonCode = error.responseJson && typeof error.responseJson.code === 'string'
    ? error.responseJson.code
    : undefined;

  return responseJsonCode === 'subscription_inactive';
}

export function isAuthInvalidFunctionInvokeError(error: unknown): boolean {
  if (!(error instanceof FunctionInvokeError)) {
    return false;
  }

  if (error.status === 401 || error.errorCode === 'auth_invalid') {
    return true;
  }

  const responseJsonCode = error.responseJson && typeof error.responseJson.code === 'string'
    ? error.responseJson.code
    : undefined;
  const responseJsonErrorCode = error.responseJson && typeof error.responseJson.errorCode === 'string'
    ? error.responseJson.errorCode
    : undefined;

  return responseJsonCode === 'auth_invalid' || responseJsonErrorCode === 'auth_invalid';
}

function normalizeFingerprintValue(value: FingerprintValue | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  const text = String(value).trim();
  return text.length > 0 ? text : undefined;
}

function normalizeFingerprintMessage(value: FingerprintValue | undefined): string | undefined {
  const text = normalizeFingerprintValue(value);
  if (!text) {
    return undefined;
  }

  return text
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function buildFunctionInvokeErrorFingerprintParts(
  functionName: FingerprintValue,
  rpcName: FingerprintValue,
  errorCode: FingerprintValue,
  errorClass: FingerprintValue,
  message: FingerprintValue,
): string[] {
  const base = ['edge-function-root-cause', normalizeFingerprintValue(functionName) ?? 'unknown-function'];

  const fingerprintParts = [
    ...base,
    normalizeFingerprintValue(rpcName),
    normalizeFingerprintValue(errorCode),
    normalizeFingerprintValue(errorClass),
    normalizeFingerprintMessage(message),
  ].filter((part): part is string => part !== undefined && part.length > 0);

  return fingerprintParts.length > base.length ? fingerprintParts : base;
}

export async function getFunctionInvokeErrorSentryFingerprint(error: unknown): Promise<string[]> {
  const functionInvokeExtra = await getFunctionInvokeErrorSentryExtra(error);
  const functionInvokeDetails = 'functionInvoke' in functionInvokeExtra ? functionInvokeExtra.functionInvoke : undefined;
  const responseMessage = functionInvokeDetails?.responseJson && typeof functionInvokeDetails.responseJson === 'object' &&
    !Array.isArray(functionInvokeDetails.responseJson)
      ? (typeof functionInvokeDetails.responseJson.error === 'string'
        ? functionInvokeDetails.responseJson.error
        : typeof functionInvokeDetails.responseJson.message === 'string'
          ? functionInvokeDetails.responseJson.message
          : undefined)
      : undefined;

  const errorMessage = responseMessage
    ?? (error instanceof Error ? error.message : '');

  return buildFunctionInvokeErrorFingerprintParts(
    functionInvokeDetails?.functionName,
    functionInvokeDetails?.rpcName,
    functionInvokeDetails?.errorCode,
    functionInvokeDetails?.errorClass,
    errorMessage,
  );
}

function getResponseFromError(error: unknown): Response | undefined {
  if (
    (error instanceof FunctionsHttpError ||
      error instanceof FunctionsRelayError) &&
    error.context instanceof Response
  ) {
    return error.context;
  }

  return undefined;
}

async function readResponseDetails(response?: Response): Promise<FunctionInvokeErrorDetails> {
  if (!response) {
    return {};
  }

  const details: FunctionInvokeErrorDetails = {
    status: response.status,
  };

  try {
    const body = await response.clone().text();
    if (body.trim().length === 0) {
      return details;
    }

    details.responseBody = body;

    try {
      const responseJson = JSON.parse(body) as ResponseJson;
      details.responseJson = responseJson;
      details.errorCode = typeof responseJson.errorCode === 'string' ? responseJson.errorCode : undefined;
      details.errorClass = typeof responseJson.errorClass === 'string' ? responseJson.errorClass : undefined;
      details.rpcName = typeof responseJson.rpcName === 'string' ? responseJson.rpcName : undefined;
      details.requestId = typeof responseJson.requestId === 'string' ? responseJson.requestId : undefined;
      details.functionName = typeof responseJson.functionName === 'string' ? responseJson.functionName : undefined;
    } catch {
      // Preserve the raw body when the response is not JSON.
    }
  } catch {
    // Keep status-only details when the body is no longer readable.
  }

  return details;
}

function getPreferredErrorMessage(
  error: unknown,
  fallbackMessage: string,
  responseDetails: FunctionInvokeErrorDetails,
): string {
  const responseJson = responseDetails.responseJson;

  if (responseJson) {
    if (typeof responseJson.error === 'string' && responseJson.error.trim().length > 0) {
      return responseJson.error;
    }

    if (typeof responseJson.message === 'string' && responseJson.message.trim().length > 0) {
      return responseJson.message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.trim().length > 0
  ) {
    return error.message;
  }

  return fallbackMessage;
}

export async function buildFunctionInvokeError(
  functionName: string,
  error: unknown,
  fallbackMessage: string,
  response?: Response,
): Promise<FunctionInvokeError> {
  // Callers may receive a typed `FunctionInvokeError` from a shared wrapper
  // (for example, an SSO step-up denial) and normalize it again at their own
  // invoke boundary. Its parsed response is already canonical; re-reading
  // only a raw FunctionsHttpError here would otherwise discard the backend
  // status/code/class/request metadata needed for Sentry grouping.
  if (error instanceof FunctionInvokeError) {
    return new FunctionInvokeError(error.message, {
      functionName: error.functionName ?? functionName,
      status: error.status,
      responseBody: error.responseBody,
      responseJson: error.responseJson,
      errorCode: error.errorCode,
      errorClass: error.errorClass,
      rpcName: error.rpcName,
      requestId: error.requestId,
      isAbort: error.isAbort,
      isFetchError: error.isFetchError,
    });
  }

  const responseDetails = await readResponseDetails(response ?? getResponseFromError(error));
  const message = getPreferredErrorMessage(error, fallbackMessage, responseDetails);

  return new FunctionInvokeError(message, {
    ...responseDetails,
    functionName: responseDetails.functionName ?? functionName,
    isAbort: isFunctionInvokeAbortError(error),
    isFetchError: isFunctionFetchError(error),
  });
}

export async function getFunctionInvokeErrorSentryExtra(
  error: unknown,
): Promise<{ functionInvoke: FunctionInvokeErrorDetails } | Record<string, never>> {
  if (error instanceof FunctionInvokeError) {
    return {
      functionInvoke: {
        functionName: error.functionName,
        status: error.status,
        responseBody: error.responseBody,
        responseJson: error.responseJson,
        errorCode: error.errorCode,
        errorClass: error.errorClass,
        rpcName: error.rpcName,
        requestId: error.requestId,
      },
    };
  }

  const responseDetails = await readResponseDetails(getResponseFromError(error));
  if (Object.keys(responseDetails).length === 0) {
    return {};
  }

  return {
    functionInvoke: responseDetails,
  };
}

function normalizeSentryTags(tags: Record<string, SentryTagValue> = {}): Record<string, string> {
  return Object.entries(tags).reduce<Record<string, string>>((accumulator, [key, value]) => {
    if (value === null || value === undefined || value === '') {
      return accumulator;
    }

    accumulator[key] = String(value);
    return accumulator;
  }, {});
}

function getFunctionInvokeErrorKind(error: unknown): string {
  if (error instanceof FunctionInvokeError && error.isFetchError) {
    return 'function_fetch_error';
  }

  if (error instanceof FunctionsFetchError) {
    return 'function_fetch_error';
  }

  if (error instanceof FunctionsHttpError) {
    return 'function_http_error';
  }

  if (error instanceof FunctionsRelayError) {
    return 'function_relay_error';
  }

  if (error instanceof FunctionInvokeError) {
    return 'function_invoke_error';
  }

  return 'function_error';
}

export async function getFunctionInvokeErrorSentryContext(
  error: unknown,
  options: FunctionInvokeSentryContextOptions = {},
): Promise<{ tags: Record<string, string>; extra: Record<string, unknown> }> {
  const functionInvokeExtra = await getFunctionInvokeErrorSentryExtra(error);
  const functionInvokeDetails = 'functionInvoke' in functionInvokeExtra ? functionInvokeExtra.functionInvoke : undefined;

  return {
    tags: normalizeSentryTags({
      ...options.tags,
      function_name: functionInvokeDetails?.functionName,
      error_kind: getFunctionInvokeErrorKind(error),
    }),
    extra: {
      ...options.extra,
      ...functionInvokeExtra,
    },
  };
}

export function getFunctionInvokeDisplayMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (error instanceof FunctionInvokeError) {
    if (
      error.responseJson &&
      typeof error.responseJson.error === 'string' &&
      error.responseJson.error.trim().length > 0
    ) {
      return error.responseJson.error;
    }

    if (
      error.responseJson &&
      typeof error.responseJson.message === 'string' &&
      error.responseJson.message.trim().length > 0
    ) {
      return error.responseJson.message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
}

function getFunctionInvokeSentryMessagePrefix(details: FunctionInvokeErrorDetails): string {
  const functionName = asTrimmedString(details.functionName);
  if (!functionName) {
    return 'Edge Function Error:';
  }

  const rpcName = asTrimmedString(details.rpcName);
  const status = typeof details.status === 'number' ? String(details.status) : '';
  const errorClass = asTrimmedString(details.errorClass);
  const errorCode = asTrimmedString(details.errorCode);

  const target = rpcName ? `${functionName}/${rpcName}` : functionName;
  const metadata = [status, errorClass, errorCode ? `(${errorCode})` : undefined]
    .filter((part): part is string => part !== undefined && part.length > 0)
    .join(' ');

  return metadata.length > 0
    ? `Edge Function Error [${target}] ${metadata}:`
    : `Edge Function Error [${target}]:`;
}

export function getFunctionInvokeSentryTitle(
  error: unknown,
  fallbackMessage: string,
): string {
  const message = getFunctionInvokeDisplayMessage(error, fallbackMessage);
  if (!(error instanceof FunctionInvokeError)) {
    return message;
  }

  const prefix = getFunctionInvokeSentryMessagePrefix(error);
  return `${prefix} ${message}`;
}
